import { User, Bot } from "lucide-react";
import clsx from "clsx";
import type { ChatMessage as ChatMessageType } from "../../types";

interface ChatMessageProps {
  message: ChatMessageType;
}

export default function ChatMessage({ message }: ChatMessageProps) {
  const isUser = message.role === "USER";

  return (
    <div
      className={clsx(
        "flex gap-3 animate-fade-in",
        isUser ? "flex-row-reverse" : "flex-row",
      )}
    >
      {/* Avatar */}
      <div
        className={clsx(
          "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
          isUser ? "bg-primary-500" : "bg-dark-card",
        )}
      >
        {isUser ? (
          <User className="w-4 h-4 text-white" />
        ) : (
          <Bot className="w-4 h-4 text-accent-cyan" />
        )}
      </div>

      {/* Message bubble */}
      <div
        className={clsx(
          "max-w-[80%] px-4 py-3 rounded-2xl",
          isUser
            ? "bg-primary-500 text-white rounded-br-md"
            : "bg-dark-card border border-dark-border text-text-primary rounded-bl-md",
        )}
      >
        <p className="text-sm whitespace-pre-wrap">{message.content}</p>

        {/* Tool calls (for assistant messages) */}
        {message.toolCalls && message.toolCalls.length > 0 && (
          <div className="mt-3 pt-3 border-t border-dark-border">
            <p className="text-xs text-text-muted mb-2">Data sources used:</p>
            <div className="flex flex-wrap gap-1">
              {message.toolCalls.map((tool, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center px-2 py-1 rounded-md bg-dark-hover text-xs text-accent-cyan"
                >
                  {formatToolName(tool.toolName)}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Timestamp */}
        <p
          className={clsx(
            "text-xs mt-2",
            isUser ? "text-primary-200" : "text-text-muted",
          )}
        >
          {formatTime(message.timestamp)}
        </p>
      </div>
    </div>
  );
}

function formatToolName(name: string): string {
  return name
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

function formatTime(timestamp: string): string {
  return new Date(timestamp).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}
